<script setup lang="ts">
import { computed } from 'vue';
import { UiText, UiAvatar, UiImage, generateInitials } from '@shopexperts/shared';
import type { PublicExpertMinimal, PackagedServiceMinimal } from '@shopexperts/shared';

interface Props {
  expert: PublicExpertMinimal;
  packagedService?: PackagedServiceMinimal;
}

const props = defineProps<Props>();

const displayRole = computed(() => {
  if (props.expert.expert_type === 'agency') {
    return 'Agency';
  }
  return props.expert.freelancer_role || 'Expert';
});

const avatarUrl = computed(() => props.expert.profile_image || undefined);

const initials = computed(() => generateInitials(props.expert.name));

const hasPackagedService = computed(() => !!props.packagedService);

const label = computed(() => {
  if (hasPackagedService.value) {
    return 'Selected Service';
  }
  return 'Preferred Expert';
});
</script>

<template>
  <div class="flex flex-col gap-2">
    <!-- Label -->
    <UiText as="span" variant="caption" class="text-se-gray-500 uppercase tracking-wider">
      {{ label }}
    </UiText>
    
    <!-- Card with Packaged Service -->
    <div v-if="hasPackagedService" class="flex gap-3 p-3 bg-se-gray-50 rounded-lg border border-se-gray-200">
      <!-- Packaged Service Thumbnail -->
      <div v-if="packagedService?.thumbnail" class="w-14 h-14 rounded-md overflow-hidden shrink-0">
        <UiImage
          :src="packagedService.thumbnail"
          :alt="packagedService.name"
          width="100%"
          height="100%"
          cover
          :rounded="false"
        />
      </div>
      
      <!-- Content -->
      <div class="flex flex-col gap-1 min-w-0">
        <!-- Packaged Service Name (Title) -->
        <UiText 
          as="span" 
          variant="label" 
          class="text-se-gray-900 truncate"
        >
          {{ packagedService?.name }}
        </UiText>
        
        <!-- Expert Info: Avatar + Name · Role -->
        <div class="flex items-center gap-2">
          <UiAvatar
            :url="avatarUrl"
            :initials="initials"
            size="xxxs"
            :responsive="false"
            class="shrink-0"
          />
          <div>
          <UiText as="span" variant="h6">
            <span>{{ expert.name }}</span>,
          </UiText>
          <UiText as="span" variant="h6">
            <span>{{ displayRole }}</span>  
          </UiText>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Card without Packaged Service (Expert only) -->
    <div v-else class="flex items-center gap-3 p-3 bg-se-gray-50 rounded-lg border border-se-gray-200">
      <!-- Avatar -->
      <UiAvatar
        :url="avatarUrl"
        :initials="initials"
        size="xs"
        :responsive="false"
        class="shrink-0"
      />
      
      <!-- Info -->
      <div class="flex flex-col min-w-0">
        <UiText as="span" variant="label" class="text-se-gray-900 truncate">
          {{ expert.name }}
        </UiText>
        <UiText as="span" variant="caption" class="text-se-gray-500">
          {{ displayRole }}
        </UiText>
      </div>
    </div>
  </div>
</template>
